import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
	AlertCircle,
	UserCircle,
	UserPen,
	CheckCircle2,
	Copy,
	XCircle,
	ArrowLeft,
	Ticket,
	Building2,
	ExternalLink,
	CreditCard,
	Loader2,
	Clock,
	Calendar,
} from 'lucide-react';
import {
	PageTitle,
	Button,
	AlertContainer,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui';
import {
	useGetUser,
	useGetClubs,
	useGetCompany,
	usePatchUser,
	useAssignClub,
	useCancelUserClubSubscription,
	// useReconcileUserClubSubscription,
} from '@/services';
import { useModal } from '@/hooks';
import { cn } from '@/lib/utils';
import { UserForm } from './components/UserForm';
import { UserPatientDetailTags } from './components/UserPatientDetailTags';
import { UserClubBenefitsBalance } from './components/UserClubBenefitsBalance';
import { UserClubTimeline } from './components/UserClubTimeline';
import { maskCPF } from '@/utils/masks';
import { formatDateWithDifference } from '@/utils/transformer';
import { buildCheckoutRedirectUrl } from '@/utils/checkoutRedirectOrigin';
import { toast } from 'react-toastify';

const CLUB_SUBSCRIPTION_STATUS_LABEL: Record<string, string> = {
	ACTIVE: 'Ativa',
	PENDING: 'Pendente',
	CANCELLED: 'Cancelada',
	EXPIRED: 'Expirada',
	SUSPENDED: 'Suspensa',
};

/** Cores de estado alinhadas a `UserClubs` (badges “Em dia” / pendente e `rose` para problema). */
type SubscriptionVisualKey =
	| 'ACTIVE'
	| 'PENDING'
	| 'CANCELLED'
	| 'EXPIRED'
	| 'SUSPENDED'
	| 'DEFAULT';

function getSubscriptionVisualKey(status: string): SubscriptionVisualKey {
	if (status === 'ACTIVE') return 'ACTIVE';
	if (status === 'PENDING') return 'PENDING';
	if (status === 'CANCELLED') return 'CANCELLED';
	if (status === 'EXPIRED') return 'EXPIRED';
	if (status === 'SUSPENDED') return 'SUSPENDED';
	return 'DEFAULT';
}

const SUBSCRIPTION_CARD_MAP: Record<
	SubscriptionVisualKey,
	{ card: string; tag: string }
> = {
	ACTIVE: {
		card: 'border-emerald-200 bg-white',
		tag: 'border border-emerald-200 bg-emerald-100 text-emerald-700',
	},
	PENDING: {
		card: 'border-amber-100 bg-white',
		tag: 'border border-amber-100 bg-amber-50 text-amber-600',
	},
	CANCELLED: {
		card: 'border-rose-200 bg-white',
		tag: 'border border-rose-100 bg-rose-50 text-rose-600',
	},
	EXPIRED: {
		card: 'border-slate-200 bg-white',
		tag: 'border border-slate-200 bg-slate-50 text-slate-600',
	},
	SUSPENDED: {
		card: 'border-amber-200 bg-white',
		tag: 'border border-amber-200 bg-amber-100 text-amber-800',
	},
	DEFAULT: {
		card: 'border-slate-200 bg-white',
		tag: 'border border-slate-200 bg-slate-100 text-slate-600',
	},
};

function formatSubscriptionTimeHm(iso: string) {
	const d = new Date(iso);
	const hh = String(d.getHours()).padStart(2, '0');
	const mm = String(d.getMinutes()).padStart(2, '0');
	return `${hh}:${mm}`;
}

function SubscriptionMetaField({
	icon: Icon,
	label,
	children,
	variant = 'default',
}: {
	icon: LucideIcon;
	label: string;
	children: ReactNode;
	variant?: 'default' | 'danger';
}) {
	const danger = variant === 'danger';
	return (
		<div className="flex gap-2.5">
			<Icon
				className={cn(
					'mt-0.5 size-4 shrink-0',
					danger ? 'text-rose-500' : 'text-zinc-400',
				)}
				strokeWidth={2}
			/>
			<div className="min-w-0">
				<p
					className={cn(
						'text-[10px] font-bold uppercase tracking-wider',
						danger ? 'text-rose-600' : 'text-zinc-400',
					)}
				>
					{label}
				</p>
				<p className="text-sm font-medium text-zinc-900">{children}</p>
			</div>
		</div>
	);
}

function formatClubPriceLine(club: Clubs.Club | undefined): string | null {
	if (!club) return null;
	const n = parseFloat(club.price || '0');
	if (Number.isNaN(n)) return null;
	const cur = club.currency === 'BRL' ? 'BRL' : 'BRL';
	const formatted = new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: cur,
	}).format(n);
	return `${formatted}/mês`;
}

function SubscriptionStatusGlyph({ status }: { status: string }) {
	const key = getSubscriptionVisualKey(status);
	const cls = 'shrink-0';
	switch (key) {
		case 'ACTIVE':
			return <CheckCircle2 size={14} className={cls} />;
		case 'PENDING':
			return <AlertCircle size={14} className={cls} />;
		case 'CANCELLED':
			return <XCircle size={14} className={cls} />;
		case 'EXPIRED':
			return <Clock size={14} className={cls} />;
		case 'SUSPENDED':
			return <AlertCircle size={14} className={cls} />;
		default:
			return <AlertCircle size={14} className={cls} />;
	}
}

const CLUB_SELECT_NONE = '__none__';

const canAdminCancelSubscription = (status: string) =>
	status === 'ACTIVE' || status === 'PENDING' || status === 'SUSPENDED';

/** Quantidade inicial de cards exibidos no histórico de assinaturas (clube). */
const CLUB_SUBSCRIPTION_LIST_INITIAL_VISIBLE = 5;

/** Quantos cards adicionar ao clicar em “Carregar mais”. */
const CLUB_SUBSCRIPTION_LIST_LOAD_MORE = 5;

const EditUser = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const [copied, setCopied] = useState(false);
	const [subscriptionListVisible, setSubscriptionListVisible] = useState(
		CLUB_SUBSCRIPTION_LIST_INITIAL_VISIBLE,
	);
	const [assignClubId, setAssignClubId] = useState('');
	const [assignGeneratedCheckoutUrl, setAssignGeneratedCheckoutUrl] =
		useState<string | null>(null);
	// const [reconcileClubId, setReconcileClubId] = useState('');
	// const [reconcileStripeId, setReconcileStripeId] = useState('');

	const { openModal, setOnConfirm, setConfirmText, setCancelText } =
		useModal();
	const { mutateAsync: assignClubAsync, isPending: assignPending } =
		useAssignClub();

	useEffect(() => {
		setAssignGeneratedCheckoutUrl(null);
	}, [assignClubId]);

	useEffect(() => {
		setSubscriptionListVisible(CLUB_SUBSCRIPTION_LIST_INITIAL_VISIBLE);
	}, [id]);
	const { mutate: cancelSubscriptionMutate, isPending: cancelSubPending } =
		useCancelUserClubSubscription();
	// const { mutate: reconcileMutate, isPending: reconcilePending } =
	//   useReconcileUserClubSubscription();

	const { data, isLoading, isError } = useGetUser(
		{ id: id || '' },
		{ enabled: !!id },
	);

	const employeeCompanyId = data?.employee?.company_id;
	const nestedCompanyName =
		data?.employee?.company?.trade_name ??
		data?.employee?.company?.legal_name;
	const shouldFetchCompanyForName =
		!!employeeCompanyId && !String(nestedCompanyName ?? '').trim();

	const { data: companyDetail, isFetching: companyNameLoading } =
		useGetCompany(employeeCompanyId ?? '', {
			enabled: shouldFetchCompanyForName,
		});

	const { data: clubsData } = useGetClubs();
	const clubs = Array.isArray(clubsData) ? clubsData : clubsData?.data ?? [];
	const clubById = useMemo(
		() => Object.fromEntries(clubs.map((c) => [c.id, c])),
		[clubs],
	);

	const clubsForSelect = useMemo(
		() =>
			[...clubs]
				.filter((c) => c.active !== false)
				.sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
		[clubs],
	);

	const { mutate, isPending } = usePatchUser(() =>
		navigate('/admin/pacientes'),
	);

	const copyId = () => {
		if (data?.id) {
			navigator.clipboard.writeText(data.id);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		}
	};

	const onSubmit = (data: Partial<Users.Update>) => {
		const { profile_photo_url, ...newData } = data;
		mutate({
			id: id as string,
			...newData,
		});
	};

	const requestCancelSubscription = (
		subscriptionId: string,
		clubLabel: string,
	) => {
		setConfirmText('Confirmar cancelamento');
		setCancelText('Voltar');
		openModal({
			title: 'Cancelar assinatura de clube',
			content: (
				<AlertContainer
					message={
						<span>
							Tem certeza que deseja cancelar a assinatura do
							clube{' '}
							<span className="font-bold text-zinc-900">
								{clubLabel}
							</span>
							? Se houver cobrança no Stripe, o cancelamento pode
							ser agendado.
						</span>
					}
				/>
			),
		});
		setOnConfirm(() => {
			cancelSubscriptionMutate(subscriptionId);
		});
	};

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="mx-auto max-w-7xl space-y-8">
				{/* Header */}
				<div className="flex items-center gap-6">
					<button
						type="button"
						onClick={() => navigate('/admin/pacientes')}
						className="group flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200 hover:shadow-sm"
					>
						<ArrowLeft
							size={20}
							className="text-stone-400 group-hover:text-stone-900"
						/>
					</button>
					<div className="flex min-w-0 flex-1 flex-col gap-1">
						<PageTitle
							text={
								data
									? `Paciente: ${data.first_name} ${
											data.last_name || ''
									  }`.trim()
									: 'Paciente'
							}
						/>
						<nav className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
							<span>PACIENTES</span>
							<span className="text-zinc-200">/</span>
							<span className="text-zinc-900">EDITAR</span>
						</nav>
					</div>
				</div>

				{isLoading ? (
					<div className="flex h-40 items-center justify-center">
						<p className="animate-pulse text-sm font-bold uppercase tracking-widest text-zinc-400">
							Carregando...
						</p>
					</div>
				) : isError || !data ? (
					<div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-zinc-200 bg-white py-24 text-center">
						<div className="mb-4 rounded-full bg-zinc-50 p-4 text-zinc-400 shadow-sm">
							<AlertCircle size={32} />
						</div>
						<h3 className="text-base font-bold text-zinc-900">
							Paciente não encontrado
						</h3>
						<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-zinc-400">
							Não foi possível localizar os dados deste paciente.
						</p>
					</div>
				) : (
					<>
						{/* Informações do usuário (estrutura alinhada a Editar profissional) */}
						<div className="overflow-hidden rounded-3xl bg-white shadow-sm">
							<div className="space-y-6 p-6 md:p-8">
								<div className="flex flex-col gap-3 md:gap-4">
									<div className="w-full space-y-4">
										<div className="flex flex-wrap items-center gap-2 text-zinc-400">
											<h2 className="text-lg font-bold text-zinc-900">
												Informações do usuário
											</h2>
											<div className="flex items-center gap-1.5 rounded-md border border-zinc-100 bg-zinc-50 px-2 py-1">
												<span className="font-mono text-xs">
													Id: {data.id}
												</span>
												<button
													type="button"
													onClick={copyId}
													className="transition-colors hover:text-zinc-900"
													aria-label="Copiar ID"
												>
													{copied ? (
														<CheckCircle2
															size={14}
															className="text-emerald-500"
														/>
													) : (
														<Copy size={14} />
													)}
												</button>
											</div>
										</div>

										<div className="flex items-center gap-5">
											<div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-zinc-200 bg-zinc-100 text-zinc-400">
												{data.profile_photo_url ? (
													<img
														src={
															data.profile_photo_url
														}
														alt=""
														className="h-full w-full object-cover"
														referrerPolicy="no-referrer"
													/>
												) : (
													<UserCircle size={32} />
												)}
											</div>
											<div className="min-w-0 space-y-1">
												<h3 className="text-base font-bold text-zinc-900">
													{data.first_name}{' '}
													{data.last_name || ''}
												</h3>
												<p className="truncate text-xs text-zinc-500">
													{data.email ?? '-'}
												</p>
												{data.cpf && (
													<p className="font-mono text-[10px] uppercase tracking-tighter text-zinc-400">
														CPF: {maskCPF(data.cpf)}
													</p>
												)}
											</div>
										</div>
									</div>

									<div className="flex w-full flex-1 flex-col gap-3">
										<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
											Perfil
										</p>
										<UserPatientDetailTags
											variant="perfil"
											user={data}
										/>
									</div>

									<div className="flex w-full flex-1 flex-col gap-3">
										<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
											Acesso
										</p>
										<UserPatientDetailTags
											variant="acesso"
											user={data}
										/>
									</div>

									<div className="flex w-full flex-1 flex-col gap-3">
										<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
											Permissões
										</p>
										<UserPatientDetailTags
											variant="permissoes"
											user={data}
										/>
									</div>
								</div>

								<div className="border-t border-zinc-100 pt-6">
									<p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
										Empresa
									</p>
									{data.employee ? (
										<div className="flex flex-col gap-4 rounded-2xl border border-zinc-100 bg-zinc-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
											<div className="flex min-w-0 items-start gap-3">
												<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cyan-900/10 text-cyan-900">
													<Building2
														className="size-5"
														aria-hidden
													/>
												</div>
												<div className="min-w-0 space-y-1">
													<p className="truncate text-sm font-bold text-zinc-900">
														{companyNameLoading ? (
															<span className="inline-flex items-center gap-2 font-normal text-zinc-400">
																<Loader2
																	className="size-4 shrink-0 animate-spin"
																	aria-hidden
																/>
																Carregando…
															</span>
														) : (
															nestedCompanyName?.trim() ||
															companyDetail?.trade_name?.trim() ||
															companyDetail?.legal_name?.trim() ||
															'(sem nome)'
														)}
													</p>
													<p className="text-xs text-zinc-500">
														Funcionário{' '}
														<span className="font-mono text-[11px] text-zinc-600">
															{data.employee.id}
														</span>
														{' · '}
														{data.employee
															.active ? (
															<span className="text-emerald-800">
																Ativo
															</span>
														) : (
															<span className="text-rose-700">
																Inativo
															</span>
														)}
													</p>
													<p className="font-mono text-[10px] text-zinc-400">
														Empresa ID:{' '}
														{
															data.employee
																.company_id
														}
													</p>
												</div>
											</div>
											{data.employee.company_id && (
												<Link
													to={`/admin/empresas/${data.employee.company_id}`}
													className="inline-flex shrink-0 items-center gap-2 self-start rounded-xl border border-zinc-200 bg-white px-4 py-2 text-xs font-bold text-zinc-800 shadow-sm transition-colors hover:bg-zinc-50 sm:self-center"
												>
													Ver empresa
													<ExternalLink className="size-3.5 opacity-60" />
												</Link>
											)}
										</div>
									) : (
										<p className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/80 px-4 py-6 text-sm text-zinc-500">
											Este paciente não está vinculado a
											nenhuma empresa.
										</p>
									)}
								</div>
							</div>
						</div>

						{/* Formulário de edição */}
						<div className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm md:p-8">
							<div className="mb-6 flex flex-wrap items-center gap-2">
								<div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-100/80">
									<UserPen className="size-4" aria-hidden />
								</div>
								<div>
									<h2 className="text-lg font-bold text-zinc-900">
										Editar informações do usuário
									</h2>
									<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
										Dados cadastrais e contato
									</p>
								</div>
							</div>
							<UserForm
								defaultValues={data}
								onSubmit={onSubmit}
								isSubmitting={isPending}
								hideFormTitle
							/>
						</div>

						{/* Clube e assinatura (admin) */}
						<div className="rounded-3xl border border-zinc-200 bg-white p-6 shadow-sm md:p-8">
							<div className="mb-6 flex flex-wrap items-center gap-2">
								<div className="flex h-9 w-9 items-center justify-center rounded-full bg-purple-50 text-purple-700">
									<Ticket className="size-4" aria-hidden />
								</div>
								<div>
									<h2 className="text-lg font-bold text-zinc-900">
										Clube e assinatura
									</h2>
									<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
										Situação atual do paciente
									</p>
								</div>
							</div>

							{(() => {
								const subs = [
									...(data.user_club_subscription ?? []),
								].sort((a, b) => {
									const ta = a.created_at
										? new Date(a.created_at).getTime()
										: 0;
									const tb = b.created_at
										? new Date(b.created_at).getTime()
										: 0;
									return tb - ta;
								});
								const employeeLinkedId =
									data.employee?.user_club_subscription_id;
								const hasActiveSubscription = subs.some(
									(s) => s.status === 'ACTIVE',
								);
								const visibleSubs = subs.slice(
									0,
									subscriptionListVisible,
								);
								const subscriptionListHasMore =
									subs.length > subscriptionListVisible;
								const subscriptionListNextBatch =
									subscriptionListHasMore
										? Math.min(
												CLUB_SUBSCRIPTION_LIST_LOAD_MORE,
												subs.length -
													subscriptionListVisible,
										  )
										: 0;

								const stripeCustomerId =
									data.stripe_customer_id?.trim() ?? '';

								return (
									<div className="space-y-6">
										<div className="rounded-2xl border border-zinc-100 bg-zinc-50/60 px-4 py-3 md:px-5">
											<p className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">
												ID do cliente Stripe
											</p>
											<div className="mt-1 flex min-h-[1.75rem] items-center gap-2">
												{stripeCustomerId ? (
													<>
														<span className="min-w-0 shrink font-mono text-sm font-semibold text-zinc-900 [overflow-wrap:anywhere]">
															{stripeCustomerId}
														</span>
														<button
															type="button"
															onClick={async () => {
																try {
																	await navigator.clipboard.writeText(
																		stripeCustomerId,
																	);
																	toast.success(
																		'ID copiado!',
																	);
																} catch {
																	toast.error(
																		'Não foi possível copiar.',
																	);
																}
															}}
															className="shrink-0 rounded-lg p-1.5 text-zinc-400 transition-colors hover:bg-zinc-200/70 hover:text-zinc-800"
															aria-label="Copiar ID do cliente Stripe"
															title="Copiar"
														>
															<Copy
																className="size-3.5"
																strokeWidth={
																	2.5
																}
															/>
														</button>
													</>
												) : (
													<span className="font-sans text-sm font-medium text-zinc-500">
														Não vinculado
													</span>
												)}
											</div>
										</div>

										<div className="rounded-2xl border border-zinc-100 bg-zinc-50/40 p-4 md:p-5">
											<p className="mb-3 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
												Ações (API admin)
											</p>
											<p className="mb-4 text-xs leading-relaxed text-zinc-500">
												<strong className="text-zinc-700">
													Vincular:
												</strong>{' '}
												gera checkout Stripe (B2C) ou
												segue o fluxo B2B2C do back —
												exige que o paciente{' '}
												<strong>
													não tenha assinatura ACTIVE
												</strong>
												.
												{/* Reconciliar Stripe (temporariamente oculto):
                        <strong className="text-zinc-700">Reconciliar:</strong>{' '}
                        cria assinatura local a partir de uma{' '}
                        <code className="rounded bg-zinc-200/80 px-1 text-[11px]">
                          sub_…
                        </code>{' '}
                        no Stripe (opcional: o back busca sozinho pelo cliente).
                        */}
											</p>

											<div className="flex flex-col gap-6 lg:flex-row">
												<div className="min-w-0 flex-1 space-y-2">
													<span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
														Vincular / checkout
													</span>
													<Select
														value={
															assignClubId ||
															CLUB_SELECT_NONE
														}
														onValueChange={(v) =>
															setAssignClubId(
																v ===
																	CLUB_SELECT_NONE
																	? ''
																	: v,
															)
														}
														disabled={
															hasActiveSubscription ||
															assignPending
														}
													>
														<SelectTrigger className="h-11 w-full max-w-md border-zinc-200 bg-white text-left text-sm">
															<SelectValue>
																{assignClubId
																	? `${
																			clubById[
																				assignClubId
																			]
																				?.name ??
																			'Clube'
																	  } (${
																			clubById[
																				assignClubId
																			]
																				?.shelf ??
																			'—'
																	  })`
																	: 'Selecione um clube'}
															</SelectValue>
														</SelectTrigger>
														<SelectContent>
															<SelectItem
																value={
																	CLUB_SELECT_NONE
																}
															>
																Selecione um
																clube
															</SelectItem>
															{clubsForSelect.map(
																(c) => (
																	<SelectItem
																		key={
																			c.id
																		}
																		value={
																			c.id
																		}
																	>
																		{c.name}
																		<span className="ml-2 text-zinc-400">
																			(
																			{
																				c.shelf
																			}
																			)
																		</span>
																	</SelectItem>
																),
															)}
														</SelectContent>
													</Select>
													<div className="mt-2 flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap">
														<Button
															type="button"
															variant="contained"
															size="small"
															text={
																assignPending
																	? 'Processando…'
																	: 'Pagar e ir'
															}
															className="h-10 rounded-xl bg-zinc-900 px-4 text-xs font-bold text-white hover:bg-black disabled:opacity-60"
															startIcon={
																assignPending ? (
																	<Loader2 className="size-3.5 animate-spin" />
																) : (
																	<CreditCard className="size-3.5 stroke-[3]" />
																)
															}
															disabled={
																!assignClubId ||
																!id ||
																hasActiveSubscription ||
																assignPending
															}
															onClick={async () => {
																if (
																	!id ||
																	!assignClubId
																)
																	return;
																try {
																	const result =
																		await assignClubAsync(
																			{
																				clubId: assignClubId,
																				userId: id,
																				returnUrl:
																					buildCheckoutRedirectUrl(
																						'/clubes',
																					),
																				redirect:
																					'none',
																			},
																		);
																	if (
																		'url' in
																			result &&
																		result.url
																	) {
																		setAssignGeneratedCheckoutUrl(
																			result.url,
																		);
																		window.open(
																			result.url,
																			'_blank',
																			'noopener,noreferrer',
																		);
																		toast.success(
																			'Pagamento aberto em nova aba.',
																		);
																	}
																} catch {
																	/* hook trata erro */
																}
															}}
														/>
														<Button
															type="button"
															variant="outline"
															size="small"
															text={
																assignPending
																	? 'Gerando…'
																	: 'Gerar e copiar'
															}
															className="h-10 rounded-xl border-zinc-200 px-4 text-xs font-bold disabled:opacity-60"
															startIcon={
																assignPending ? (
																	<Loader2 className="size-3.5 animate-spin" />
																) : (
																	<Copy className="size-3.5 stroke-[3]" />
																)
															}
															disabled={
																!assignClubId ||
																!id ||
																hasActiveSubscription ||
																assignPending
															}
															onClick={async () => {
																if (
																	!id ||
																	!assignClubId
																)
																	return;
																try {
																	const result =
																		await assignClubAsync(
																			{
																				clubId: assignClubId,
																				userId: id,
																				returnUrl:
																					buildCheckoutRedirectUrl(
																						'/clubes',
																					),
																				redirect:
																					'none',
																			},
																		);
																	if (
																		'url' in
																			result &&
																		result.url
																	) {
																		setAssignGeneratedCheckoutUrl(
																			result.url,
																		);
																		try {
																			await navigator.clipboard.writeText(
																				result.url,
																			);
																			toast.success(
																				'URL copiada!',
																			);
																		} catch {
																			toast.error(
																				'Não foi possível copiar a URL.',
																			);
																		}
																	} else {
																		setAssignGeneratedCheckoutUrl(
																			null,
																		);
																	}
																} catch {
																	/* hook trata erro */
																}
															}}
														/>
													</div>
													{assignGeneratedCheckoutUrl ? (
														<Button
															type="button"
															variant="outline"
															size="small"
															text="Copiar link"
															className="mt-4 h-9 rounded-xl border-zinc-200 px-4 text-xs font-bold"
															startIcon={
																<Copy className="size-3.5 stroke-[3]" />
															}
															onClick={async () => {
																try {
																	await navigator.clipboard.writeText(
																		assignGeneratedCheckoutUrl,
																	);
																	toast.success(
																		'URL copiada!',
																	);
																} catch {
																	toast.error(
																		'Não foi possível copiar a URL.',
																	);
																}
															}}
														/>
													) : null}
													{hasActiveSubscription && (
														<p className="text-[11px] text-amber-800">
															Já existe assinatura
															ativa. Cancele antes
															de vincular outra.
														</p>
													)}
												</div>

												{/* Reconciliar Stripe — temporariamente oculto
                        <div className="hidden w-px shrink-0 bg-zinc-200 lg:block" />

                        <div className="min-w-0 flex-1 space-y-2">
                          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                            Reconciliar Stripe
                          </span>
                          <Select
                            value={reconcileClubId || CLUB_SELECT_NONE}
                            onValueChange={(v) =>
                              setReconcileClubId(v === CLUB_SELECT_NONE ? '' : v)
                            }
                            disabled={hasActiveSubscription || reconcilePending}
                          >
                            <SelectTrigger className="h-11 w-full max-w-md border-zinc-200 bg-white text-left text-sm">
                              <SelectValue>
                                {reconcileClubId
                                  ? clubById[reconcileClubId]?.name ?? 'Clube'
                                  : 'Selecione o clube'}
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={CLUB_SELECT_NONE}>
                                Selecione o clube
                              </SelectItem>
                              {clubsForSelect.map((c) => (
                                <SelectItem key={c.id} value={c.id}>
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <input
                            type="text"
                            placeholder="stripe_subscription_id (opcional, ex: sub_…)"
                            value={reconcileStripeId}
                            onChange={(e) => setReconcileStripeId(e.target.value)}
                            className="h-11 w-full max-w-md rounded-xl border border-zinc-200 bg-white px-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300"
                            disabled={hasActiveSubscription || reconcilePending}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="small"
                            text={
                              reconcilePending
                                ? 'Reconciliando…'
                                : 'Reconciliar assinatura'
                            }
                            className="mt-2 h-10 rounded-xl text-xs font-bold"
                            disabled={
                              !reconcileClubId ||
                              !id ||
                              hasActiveSubscription ||
                              reconcilePending
                            }
                            onClick={() => {
                              if (!id || !reconcileClubId) return;
                              reconcileMutate({
                                clubId: reconcileClubId,
                                userId: id,
                                stripe_subscription_id:
                                  reconcileStripeId.trim() || undefined,
                              });
                            }}
                          />
                        </div>
                        */}
											</div>
										</div>

										{subs.length === 0 ? (
											<p className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/80 px-4 py-8 text-center text-sm text-zinc-500">
												Nenhuma assinatura de clube
												registrada para este paciente.
											</p>
										) : null}

										{subs.length > 0 ? (
											<div className="space-y-3">
												<ul className="space-y-3">
													{visibleSubs.map((sub) => {
														const vKey =
															getSubscriptionVisualKey(
																sub.status,
															);
														const styles =
															SUBSCRIPTION_CARD_MAP[
																vKey
															];
														const clubName =
															sub.club?.name ??
															clubById[
																sub.club_id
															]?.name ??
															'Clube (ID não resolvido)';
														const label =
															CLUB_SUBSCRIPTION_STATUS_LABEL[
																sub.status
															] ?? sub.status;
														const isEmployeeLink =
															employeeLinkedId ===
															sub.id;
														const shortSubId = `${sub.id.slice(
															0,
															8,
														)}…`;
														const clubResolved =
															clubById[
																sub.club_id
															];
														const priceLine =
															formatClubPriceLine(
																clubResolved,
															);
														const showPriceInFooter =
															!!priceLine &&
															(sub.status ===
																'ACTIVE' ||
																sub.status ===
																	'PENDING');
														const showSubscriptionFooter =
															showPriceInFooter ||
															sub.status ===
																'PENDING' ||
															canAdminCancelSubscription(
																sub.status,
															);

														return (
															<li
																key={sub.id}
																className={cn(
																	'flex flex-col gap-4 rounded-3xl border p-5 shadow-sm transition-all',
																	styles.card,
																)}
															>
																<div className="flex min-w-0 flex-1 flex-col gap-4">
																	<div className="flex flex-col gap-3 md:grid md:grid-cols-[1fr_auto] md:items-start md:gap-x-4">
																		<h3 className="truncate text-base font-bold text-zinc-900 md:text-lg">
																			{
																				clubName
																			}
																		</h3>
																		<div
																			className={cn(
																				'inline-flex w-full shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-[10px] font-bold md:w-auto md:justify-self-end',
																				styles.tag,
																			)}
																		>
																			<SubscriptionStatusGlyph
																				status={
																					sub.status
																				}
																			/>
																			<span className="leading-tight md:text-xs">
																				{
																					label
																				}
																			</span>
																		</div>

																		<div className="flex flex-wrap items-center gap-2 md:col-span-2 md:col-start-1 md:row-start-2">
																			<div className="flex size-8 shrink-0 items-center justify-center rounded-lg border border-zinc-200 bg-zinc-100">
																				<Ticket
																					size={
																						14
																					}
																					className="text-zinc-500"
																					aria-hidden
																				/>
																			</div>
																			<span className="flex flex-wrap items-center gap-x-1 text-xs font-medium text-zinc-600">
																				Assinatura{' '}
																				<span className="inline-flex items-center gap-0.5">
																					<span className="font-mono text-[11px] text-zinc-600">
																						{
																							shortSubId
																						}
																					</span>
																					<button
																						type="button"
																						className="rounded p-0.5 text-zinc-400 transition-colors hover:bg-zinc-200/70 hover:text-zinc-700"
																						aria-label="Copiar ID da assinatura"
																						title="Copiar ID completo"
																						onClick={async () => {
																							try {
																								await navigator.clipboard.writeText(
																									sub.id,
																								);
																								toast.success(
																									'ID da assinatura copiado!',
																								);
																							} catch {
																								toast.error(
																									'Não foi possível copiar.',
																								);
																							}
																						}}
																					>
																						<Copy className="size-3.5 stroke-[2.5]" />
																					</button>
																				</span>
																				{isEmployeeLink && (
																					<span className="ml-2 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold text-purple-800">
																						Vínculo
																						empresa
																					</span>
																				)}
																			</span>
																		</div>
																	</div>

																	<div className="grid gap-6 border-t border-zinc-200/80 pt-4 sm:grid-cols-2 sm:gap-8">
																		<div className="flex flex-col gap-4">
																			{sub.created_at ? (
																				<>
																					<SubscriptionMetaField
																						icon={
																							Calendar
																						}
																						label="Criada em"
																					>
																						{formatDateWithDifference(
																							sub.created_at,
																						)}
																					</SubscriptionMetaField>
																					<SubscriptionMetaField
																						icon={
																							Clock
																						}
																						label="Horário"
																					>
																						{formatSubscriptionTimeHm(
																							sub.created_at,
																						)}
																					</SubscriptionMetaField>
																				</>
																			) : null}
																		</div>
																		<div className="flex flex-col gap-4">
																			{sub.current_period_start ? (
																				<SubscriptionMetaField
																					icon={
																						Calendar
																					}
																					label="Início período atual"
																				>
																					{formatDateWithDifference(
																						sub.current_period_start,
																					)}
																				</SubscriptionMetaField>
																			) : null}
																			{sub.current_period_end ? (
																				<SubscriptionMetaField
																					icon={
																						Calendar
																					}
																					label="Fim período atual"
																					variant="danger"
																				>
																					{formatDateWithDifference(
																						sub.current_period_end,
																					)}
																				</SubscriptionMetaField>
																			) : null}
																		</div>
																	</div>

																	{sub.cancelled_reason ? (
																		<div className="flex items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50/90 p-4">
																			<div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
																				<AlertCircle className="size-4" />
																			</div>
																			<p className="min-w-0 flex-1 text-sm leading-snug text-rose-800">
																				<span className="font-bold text-rose-900">
																					Motivo
																					cancelamento:
																				</span>{' '}
																				{
																					sub.cancelled_reason
																				}
																			</p>
																		</div>
																	) : null}

																	{showSubscriptionFooter ? (
																		<div className="flex w-full flex-col gap-3 border-t border-zinc-200/80 pt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
																			<div className="flex min-w-0 flex-1 flex-col gap-3">
																				{showPriceInFooter ? (
																					<div>
																						<p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">
																							Preço
																						</p>
																						<p className="text-base font-bold text-zinc-900">
																							{
																								priceLine
																							}
																						</p>
																					</div>
																				) : null}
																				{sub.status ===
																					'PENDING' &&
																				sub.stripe_checkout_session_url ? (
																					<Button
																						type="button"
																						variant="outline"
																						size="small"
																						text="Copiar link de pagamento"
																						className="h-9 w-full rounded-xl border-amber-200 bg-amber-50 px-3 text-[11px] font-bold text-amber-900 hover:bg-amber-100 sm:w-auto"
																						startIcon={
																							<Copy className="size-3 stroke-[3]" />
																						}
																						onClick={async () => {
																							try {
																								await navigator.clipboard.writeText(
																									sub.stripe_checkout_session_url!,
																								);
																								toast.success(
																									'Link copiado!',
																								);
																							} catch {
																								toast.error(
																									'Não foi possível copiar o link.',
																								);
																							}
																						}}
																					/>
																				) : null}
																				{sub.status ===
																					'PENDING' &&
																				!sub.stripe_checkout_session_url ? (
																					<p className="max-w-xl text-[11px] leading-relaxed text-amber-900">
																						Pagamento
																						pendente,
																						mas
																						sem
																						link
																						de
																						checkout
																						salvo.
																						Gere
																						um
																						novo
																						link
																						com
																						“Pagar
																						e
																						ir”
																						ou
																						“Gerar
																						e
																						copiar”
																						acima
																						para
																						o
																						mesmo
																						clube.
																					</p>
																				) : null}
																			</div>
																			{canAdminCancelSubscription(
																				sub.status,
																			) ? (
																				<Button
																					type="button"
																					variant="outline"
																					size="small"
																					text={
																						cancelSubPending
																							? 'Cancelando…'
																							: 'Cancelar assinatura'
																					}
																					className="h-9 w-full shrink-0 rounded-xl border-rose-200 bg-white text-xs font-bold text-rose-800 hover:bg-rose-50 sm:w-auto"
																					disabled={
																						cancelSubPending
																					}
																					onClick={() =>
																						requestCancelSubscription(
																							sub.id,
																							clubName,
																						)
																					}
																				/>
																			) : null}
																		</div>
																	) : null}
																</div>
															</li>
														);
													})}
												</ul>
												{subscriptionListHasMore ? (
													<div className="flex justify-center pt-1">
														<Button
															type="button"
															variant="outline"
															text={`Carregar mais ${subscriptionListNextBatch}`}
															className="h-10 rounded-xl border-zinc-200 bg-white text-xs font-bold text-zinc-700 hover:bg-zinc-50"
															onClick={() =>
																setSubscriptionListVisible(
																	(n) =>
																		n +
																		CLUB_SUBSCRIPTION_LIST_LOAD_MORE,
																)
															}
														/>
													</div>
												) : null}
											</div>
										) : null}

										{id ? (
											<>
												<UserClubBenefitsBalance
													userId={id}
													subscriptions={subs}
													clubById={clubById}
												/>
												<UserClubTimeline
													userId={id}
													subscriptions={subs}
													clubById={clubById}
												/>
											</>
										) : null}
									</div>
								);
							})()}
						</div>
					</>
				)}
			</div>
		</section>
	);
};

export { EditUser };
